import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { Box, Breadcrumbs, Button, Card, CardContent, Typography } from '@material-ui/core'
import Page from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import { BreadcrumbLink } from '../../../components/shared/breadcrumbLink/breadcrumbLink'
import { LoadingCard } from '../../../components/shared/loadingCard/loadingCard'
import { CardEmptyContent } from '../../../components/shared/cardEmptyContent/cardEmptyContent'
import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  FormDialogMode,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import {
  createContractOrders,
  DeleteContractOrders,
  getContractOrders,
  updateContractOrders
} from '../contracts.service'
import { ContractOrdersModel } from '../../../api/Argentia/endpoints/contracts/model/contracts'
import { getCustomerDomain } from '../../../redux'
import { Delete, Edit } from '@material-ui/icons'
import { useSelector } from 'react-redux'
import { selectActiveContract, selectClientData } from '../../../redux/selectors/client.selectors'
import { getLinkClientText } from '../../clients/client.helper'
import { FormDialogDefinitionProps, useConfirmationDialog, useFormDialog } from '../../../hooks'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { mapContractOrdersModelToRequest } from '../contracts.mapper'
import { CardWithTransition } from '../../../components/shared/cardWithTransition/CardWithTransition'
import moment from 'moment'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'

interface ContractOrdersPageProps {
  location?: {
    state: {
      contractID?: number
    }
  }
}

interface ContractOrdersFormValidation {
  startDate: boolean
  endDate: boolean
}

const initialFormValidation: ContractOrdersFormValidation = {
  startDate: true,
  endDate: true
}

export const ContractOrdersPage: React.FC<ContractOrdersPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'contracts', 'enums'])
  const { info, error } = useNotify()
  const history = useHistory()
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const activeClient = useSelector(selectClientData)
  const activeContract = useSelector(selectActiveContract)

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [contractOrders, setContractOrders] = useState<ContractOrdersModel[]>([])
  const [maxVersion, setMaxVersion] = useState<number>(0)
  const [formIsValid, setFormIsValid] =
    useState<ContractOrdersFormValidation>(initialFormValidation)

  const contractId = props.location?.state.contractID
  if (contractId === undefined) history.goBack

  // #region Handlers Starts
  const onAddActionHandler = async (): Promise<boolean> => {
    openForm({
      definition: contractOrdersFormDefinition,
      title: t('contracts:components.contractOrders.forms.add.title'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      inputData: { contractId: contractId, orderVersion: maxVersion },
      onClose: onAddFormCloseHandler
    })
    return true
  }

  const onAddFormCloseHandler = async (formData: ContractOrdersModel) => {
    if (formData) {
      if (missingFieldsFormWithRequiredText(contractOrdersFormDefinition?.sections!, formData)) {
        error(t('contracts:components.contractOrders.notifications.incompleteForm'))
        return
      }
      const request = mapContractOrdersModelToRequest(formData)
      await createContractOrders(contractId!, request).then(response => {
        if (response.success) {
          fetchContractOrders()
          info(t('contracts:components.contractOrders.notifications.saveSuccess'))
        } else {
          error(t('contracts:components.contractOrders.notifications.saveError'))
        }
      })
    }
    closeForm()
  }

  const onEditActionHandler = (formData: ContractOrdersModel) => {
    openForm({
      definition: contractOrdersFormDefinition,
      title: t('contracts:components.contractOrders.forms.add.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: { ...formData },
      onClose: onEditFormCloseHandler
    })
  }

  const onEditFormCloseHandler = async (formData: ContractOrdersModel) => {
    if (formData) {
      if (missingFieldsFormWithRequiredText(contractOrdersFormDefinition?.sections!, formData)) {
        error(t('contracts:components.contractOrders.notifications.incompleteForm'))
        return
      }
      const request = mapContractOrdersModelToRequest(formData)
      await updateContractOrders(contractId!, formData.contractOrderId!, request).then(response => {
        if (response.success) {
          fetchContractOrders()
          info(t('contracts:components.contractOrders.notifications.saveSuccess'))
        } else {
          error(t('contracts:components.contractOrders.notifications.saveError'))
        }
      })
    }
    closeForm()
  }

  const onDeleteActionHandler = (formData: ContractOrdersModel) => {
    getConfirmation(ConfirmationDialogs.DELETE).then(async () => {
      await DeleteContractOrders(contractId!, formData.contractOrderId!).then(response => {
        if (response.success) {
          fetchContractOrders()
          info(t('contracts:components.contractOrders.notifications.saveSuccess'))
        } else {
          error(t('contracts:components.contractOrders.notifications.saveError'))
        }
      })
      closeConfirmation()
    })
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  const getMaxVersionNumber = (contractOrders: ContractOrdersModel[]): number => {
    return contractOrders.reduce((max, order) => Math.max(max, order.orderVersion), 0)
  }

  const datesAreValid = useCallback(
    (startDate: Date | undefined, endDate: Date | undefined): boolean => {
      if (!startDate || !endDate) {
        return true
      }
      return !(moment(startDate).isAfter(endDate) || moment(endDate).isBefore(startDate))
    },
    []
  )

  const updateFormValidation = useCallback((key: 'startDate' | 'endDate') => {
    setFormIsValid(prevState => ({
      ...prevState,
      [key]: false
    }))
  }, [])

  const resetFormDatesValidations = useCallback(() => {
    setFormIsValid(prevState => ({
      ...prevState,
      startDate: true,
      endDate: true
    }))
  }, [])
  // #endregion Functions Ends

  // #region Form Definition Starts
  const contractOrdersFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'CONTRACT_ORDERS_FORM',
        sections: [
          {
            inputs: [
              {
                name: 'contractOrderId',
                display: false
              },
              {
                name: 'contractId',
                display: false
              },
              {
                name: 'orderNm',
                label: t('contracts:components.contractOrders.forms.inputs.orderNm'),
                type: DataType.TEXT,
                required: true
              },
              {
                name: 'orderVersion',
                label: t('contracts:components.contractOrders.forms.inputs.orderVersion'),
                type: DataType.NUMBER,
                customDecimalScale: 0,
                required: true,
                readOnly: true
              },
              {
                name: 'startDate',
                label: t('contracts:components.contractOrders.forms.inputs.startDate'),
                type: DataType.DATE,
                errorState: !formIsValid.startDate,
                errorText: t('contracts:components.contractOrders.forms.validations.startDate'),
                required: true,
                onChange: (value, formState) => {
                  const formData = { ...formState } as ContractOrdersModel
                  if (datesAreValid(moment(value).toDate(), formData.endDate)) {
                    resetFormDatesValidations()
                  } else {
                    updateFormValidation('startDate')
                  }
                }
              },
              {
                name: 'endDate',
                label: t('contracts:components.contractOrders.forms.inputs.endDate'),
                type: DataType.DATE,
                required: true,
                errorState: !formIsValid.endDate,
                errorText: t('contracts:components.contractOrders.forms.validations.endDate'),
                onChange: (value, formState) => {
                  const formData = { ...formState } as ContractOrdersModel
                  if (datesAreValid(formData.startDate, moment(value).toDate())) {
                    resetFormDatesValidations()
                  } else {
                    updateFormValidation('endDate')
                  }
                }
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxVersion, formIsValid, datesAreValid, updateFormValidation, resetFormDatesValidations])

  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const options = React.useMemo((): DataTableOptions => {
    return {
      i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
      search: true,
      filterType: FilterType.ADVANCED,
      download: true,
      canEdit: false,
      canDelete: false,
      canInsert: true,
      customInsertEditModal: true,
      pagination: true,
      analytics: {
        customer: getCustomerDomain(),
        view: t('contracts:components.contractOrders.dataTable.analytics')
      },
      onRowInsert: onAddActionHandler,
      rowCustomActions: [
        {
          icon: <Edit fontSize="small" />,
          tooltip: t('contracts:components.contractOrders.dataTable.actions.edit'),
          onClick: onEditActionHandler,
          userPermission: UserPermission.EDIT
        },
        {
          icon: <Delete fontSize="small" />,
          tooltip: t('contracts:components.contractOrders.dataTable.actions.delete'),
          onClick: onDeleteActionHandler,
          userPermission: UserPermission.EDIT
        }
      ]

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [maxVersion])

  const columns: DataTableColumn[] = [
    {
      name: 'contractOrderId',
      display: false
    },
    {
      name: 'contractId',
      display: false
    },
    {
      name: 'orderNm',
      label: t('contracts:components.contractOrders.dataTable.columns.orderNm'),
      type: DataType.TEXT
    },
    {
      name: 'orderVersion',
      label: t('contracts:components.contractOrders.dataTable.columns.orderVersion'),
      type: DataType.TEXT
    },
    {
      name: 'startDate',
      label: t('contracts:components.contractOrders.dataTable.columns.startDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('contracts:components.contractOrders.dataTable.columns.endDate'),
      type: DataType.DATE
    }
  ]
  // #endregion DataTable Config Ends

  // #region Hooks Starts

  const fetchContractOrders = useCallback(async () => {
    setIsFetching(true)
    await getContractOrders(contractId)
      .then(response => {
        if (response.success && response.data) {
          setContractOrders(response.data)
          setMaxVersion(getMaxVersionNumber(response.data) + 1)
        } else {
          error(t('contracts:components.contractOrders.notifications.fetchError'))
        }
      })
      .finally(() => {
        setIsFetching(false)
      })
  }, [])

  useEffect(() => {
    contractId && fetchContractOrders()
  }, [contractId, fetchContractOrders])

  useEffect(() => {
    updateFormDefinition(contractOrdersFormDefinition)
  }, [contractOrdersFormDefinition])
  // #endregion Hooks Ends

  return (
    <Page fullWidth={true}>
      <PageHeader title={t('contracts:components.contractOrders.title')}>
        <Button variant="text" color="primary" onClick={history.goBack}>
          {t('contracts:components.contractOrders.actions.back')}
        </Button>
      </PageHeader>
      <PageCenterColumn>
        <Box mb={1}>
          <Breadcrumbs separator=">" aria-label="breadcrumb">
            {activeClient && (
              <BreadcrumbLink
                key="1"
                toPathName="/client/file"
                state={{ clientID: activeClient?.id }}
              >
                {`${t('contracts:components.contractOrders.breadcrumbs.client', { clientText: getLinkClientText(activeClient), documentID: activeClient?.documentID })}`}
              </BreadcrumbLink>
            )}
            <Typography key="2" color="primary" variant="subtitle2">
              {t('contracts:components.contractOrders.breadcrumbs.contract', {
                contractCode: activeContract?.contractCode
              })}
            </Typography>
          </Breadcrumbs>
        </Box>
        {isFetching ? (
          <LoadingCard />
        ) : contractOrders.length === 0 ? (
          <CardEmptyContent
            title={t('contracts:components.contractOrders.emptyCard.title')}
            caption={t('contracts:components.contractOrders.emptyCard.caption')}
          >
            <Button size="small" color="primary" variant="contained" onClick={onAddActionHandler}>
              {t('contracts:components.contractOrders.emptyCard.buttonLabel')}
            </Button>
          </CardEmptyContent>
        ) : (
          <CardWithTransition raised show={true}>
            <CardContent>
              <DataTable data={contractOrders} options={options} columns={columns} />
            </CardContent>
          </CardWithTransition>
        )}
      </PageCenterColumn>
      <CustomFormDialog {...formProps} />
    </Page>
  )
}
